import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import Chart from "react-apexcharts";

/* ─────────────────────────────────────────────────────────────
   easeOutBack — overshoots the target slightly then settles.
   Applied per bar value each RAF frame to produce a swing feel.
───────────────────────────────────────────────────────────── */
const swingEase = (t) => {
  const s = 1.70158;
  const u = t - 1;
  return u * u * ((s + 1) * u + s) + 1;
};

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
const ApexStackedColumnV2 = ({
  series = [],
  categories = [],
  colors,
  height = 300,
  columnWidth = "45%",
  yAxisTitle,
  yMin = 0,
  yMax,
  yTickAmount,
  yLabelFormatter,
  tooltipYFormatter,
  showDataLabels = false,
  showToolbar = false,
  xLabelStyle = {},
  gridBorderColor = "#ececec",
  extraOptions = {},
  chartId,
  legendItems,
  legendJustify = "center",
  onDataPointSelection,
  selectedIndex = null,
}) => {

  const COLUMN_SLOT_PX = 250; // fixed px per category; chart scrolls when wider than container

  /* ─────────────────────────────────────────────────────────────
     Legend toggle state
  ───────────────────────────────────────────────────────────── */
  const [hiddenSet, setHiddenSet] = useState(new Set());

  /* displaySeries is only used for the initial render and to
     reset after an external series change. During legend animations
     the chart is updated imperatively via updateSeries(). */
  const [displaySeries, setDisplaySeries] = useState(series);

  /* Stable refs so the tooltip callback always reads fresh values without
     needing the options object to be recreated on every state change. */
  const hiddenSetRef          = useRef(hiddenSet);
  const legendItemsRef        = useRef(legendItems);
  const tooltipYFormatterRef  = useRef(tooltipYFormatter);

  useEffect(() => { hiddenSetRef.current         = hiddenSet;         }, [hiddenSet]);
  useEffect(() => { legendItemsRef.current        = legendItems;        }, [legendItems]);
  useEffect(() => { tooltipYFormatterRef.current  = tooltipYFormatter;  }, [tooltipYFormatter]);

  /* ─────────────────────────────────────────────────────────────
     RAF tween state
     currentSeriesRef tracks live bar heights so mid-animation
     interrupts start from the correct position.
  ───────────────────────────────────────────────────────────── */
  const animFrameRef      = useRef(null);
  const currentSeriesRef  = useRef(series);

  /* ─────────────────────────────────────────────────────────────
     Sync when external series changes (e.g. date filter)
  ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    currentSeriesRef.current = series;
    setDisplaySeries(series);
    setHiddenSet(new Set());
  }, [series]);

  /* ─────────────────────────────────────────────────────────────
     Stores the chart context so we can call updateSeries()
     imperatively during each RAF frame.
  ───────────────────────────────────────────────────────────── */
  const chartContextRef = useRef(null);

  /* ─────────────────────────────────────────────────────────────
     Core tween — interpolates from the live bar heights to
     targetSeries using the swing (easeOutBack) curve.
     Pushes each frame directly to ApexCharts without a React
     re-render, so there is no double-animation from dynamicAnimation.
  ───────────────────────────────────────────────────────────── */
  const animateToSeries = useCallback((targetSeries, duration = 550) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    const fromSeries = currentSeriesRef.current.map((s) => ({ ...s, data: [...s.data] }));
    const startTime  = performance.now();

    const step = (now) => {
      const rawT = Math.min((now - startTime) / duration, 1);
      const p    = swingEase(rawT);

      const interpolated = fromSeries.map((s, si) => ({
        ...s,
        data: s.data.map((fromVal, di) => {
          const toVal = targetSeries[si]?.data?.[di] ?? 0;
          const raw = fromVal + (toVal - fromVal) * p;
          // Round to 2 dp — enough for smooth animation, avoids floating-point
          // noise that would bleed into y-axis tick positions.
          return Math.max(0, Math.round(raw * 100) / 100);
        }),
      }));

      currentSeriesRef.current = interpolated;

      // Push directly to ApexCharts — no React state update each frame
      chartContextRef.current?.updateSeries?.(interpolated, false);

      if (rawT < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        // Snap to exact target and sync React state
        currentSeriesRef.current = targetSeries;
        chartContextRef.current?.updateSeries?.(targetSeries, false);
        setDisplaySeries(targetSeries);
      }
    };

    animFrameRef.current = requestAnimationFrame(step);
  }, []);

  /* ─────────────────────────────────────────────────────────────
     On legend toggle: build target and hand off to the tween.
  ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!legendItems || legendItems.length === 0) return;

    const target = series.map((s) =>
      hiddenSet.has(s.name)
        ? { ...s, data: s.data.map(() => 0) }
        : s
    );
    animateToSeries(target);
  }, [hiddenSet, series, legendItems, animateToSeries]);

  /* ─────────────────────────────────────────────────────────────
     Legend click
  ───────────────────────────────────────────────────────────── */
  const handleLegendClick = useCallback((name) => {
    setHiddenSet((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);

  /* ─────────────────────────────────────────────────────────────
     Column hover highlight
  ───────────────────────────────────────────────────────────── */
  const hoveredIndexRef = useRef(null);

  const addColumnHighlight = useCallback((dataPointIndex, chartContext) => {
    const root = chartContext?.el;
    if (!root || dataPointIndex < 0) return;

    root.querySelectorAll(".hover-stack").forEach((el) => {
      el.classList.remove("hover-stack");
      el.style.filter = "";
    });

    root
      .querySelectorAll(`.apexcharts-bar-area[j='${dataPointIndex}']`)
      .forEach((el) => {
        el.classList.add("hover-stack");
        el.style.filter = "brightness(1.35)";
      });
  }, []);

  const removeColumnHighlight = useCallback((chartContext) => {
    const root = chartContext?.el;
    if (!root) return;
    root.querySelectorAll(".hover-stack").forEach((el) => {
      el.classList.remove("hover-stack");
      el.style.filter = "";
    });
  }, []);

  useEffect(() => {
    if (selectedIndex === null) {
      removeColumnHighlight(chartContextRef.current);
    }
  }, [selectedIndex, removeColumnHighlight]);


  /* ─────────────────────────────────────────────────────────────
     Apex options
  ───────────────────────────────────────────────────────────── */
  const options = useMemo(() => {
    const hasLegend = legendItems && legendItems.length > 0;

    const base = {
      chart: {
        id: chartId,
        type: "bar",
        stacked: true,
        toolbar: { show: showToolbar },
        animations: {
          enabled: true,
          speed: 500,
          easing: "easeinout",
          animateGradually: { enabled: false },
          // Disabled — legend toggle animation is handled by the RAF tween
          dynamicAnimation: { enabled: false },
        },
        events: {
          // Capture context on mount so the tween can call updateSeries()
          // even before the user has hovered the chart.
          mounted: (chartContext) => {
            chartContextRef.current = chartContext;
          },
          mouseMove: (event, chartContext, config) => {
            chartContextRef.current = chartContext;
            if (!hasLegend) return;
            const idx = config.dataPointIndex;
            if (idx >= 0) {
              if (idx !== hoveredIndexRef.current) {
                hoveredIndexRef.current = idx;
                addColumnHighlight(idx, chartContext);
              }
            } else {
              hoveredIndexRef.current = null;
              removeColumnHighlight(chartContext);
            }
          },
          mouseLeave: (_e, chartContext) => {
            chartContextRef.current = chartContext;
            hoveredIndexRef.current = null;
            removeColumnHighlight(chartContext);
          },
          ...(onDataPointSelection && {
            dataPointSelection: (event, chartContext, config) => {
              const idx = config.dataPointIndex;
              if (idx === undefined || idx < 0) return;
              onDataPointSelection(event, chartContext, config);
            },
          }),
        },
      },
      states: {
        active: {
          filter: { type: "none" },
        },
      },
      plotOptions: { bar: { horizontal: false, columnWidth } },
      dataLabels: { enabled: showDataLabels },
      xaxis: { categories, labels: { style: { fontSize: "12px", ...xLabelStyle } } },
      yaxis: {
        min: yMin,
        ...(yMax !== undefined && { max: yMax }),
        ...(yTickAmount && { tickAmount: yTickAmount }),
        labels: {
          // Default: round to nearest integer to prevent decimal y-axis labels
          // that appear when the RAF tween pushes fractional values imperatively.
          formatter: yLabelFormatter ?? ((val) => String(Math.round(val))),
        },
        ...(yAxisTitle && {
          title: {
            text: yAxisTitle,
            style: { fontSize: "12px", fontWeight: 400, color: "#888" },
          },
        }),
      },
      grid: { borderColor: gridBorderColor },
      legend: { show: false },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        followCursor: true,
        // Custom tooltip so rows follow legendItems order (Discharging → Charging →
        // Idle → Offline) regardless of the series array order used for stacking.
        // Uses native ApexCharts class names so ApexCharts' own CSS styles it
        // identically to the BreakdownChart's native shared tooltip.
        custom: ({ dataPointIndex, w }) => {
          const items  = legendItemsRef.current;
          const hidden = hiddenSetRef.current;
          const fmt    = tooltipYFormatterRef.current;

          // Build name → value map from the current display series
          const valueMap = {};
          w?.config?.series?.forEach((s) => {
            valueMap[s.name] = s.data?.[dataPointIndex] ?? 0;
          });

          const label = w?.globals?.categoryLabels?.[dataPointIndex]
                     ?? w?.globals?.labels?.[dataPointIndex]
                     ?? "";

          const rows = (items ?? [])
            .filter((item) => !hidden.has(item.name))
            .map((item) => {
              const val       = valueMap[item.name] ?? 0;
              const formatted = fmt ? fmt(val) : `${val}`;
              // display:flex overrides the default display:none on .apexcharts-tooltip-series-group
              return `
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="display:flex;">
                  <span class="apexcharts-tooltip-marker" shape="circle" style="color:${item.color};"></span>
                  <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                      <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                      <span class="apexcharts-tooltip-text-y-value">${formatted}</span>
                    </div>
                  </div>
                </div>`;
            })
            .join("");

          return `
            <div class="apexcharts-tooltip-title">${label}</div>
            ${rows}`;
        },
      },
      fill: { opacity: 1 },
    };

    if (colors) base.colors = colors;
    if (onDataPointSelection) base.chart.selection = { enabled: false };
    return base;
  }, [
    chartId, categories, colors, columnWidth, legendItems,
    addColumnHighlight, removeColumnHighlight,
    onDataPointSelection, showToolbar, showDataLabels,
    xLabelStyle, gridBorderColor, yMin, yMax, yTickAmount, yLabelFormatter, yAxisTitle,
  ]);

  /* ─────────────────────────────────────────────────────────────
     Render
  ───────────────────────────────────────────────────────────── */
  return (
    <Box>
      {/* Legend */}
      {legendItems && (
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: legendJustify, gap: 2, pt: 2 }}>
          {legendItems.map((item) => {
            const isVisible = !hiddenSet.has(item.name);
            return (
              <Box
                key={item.name}
                onClick={() => handleLegendClick(item.name)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  userSelect: "none",
                  opacity: isVisible ? 1 : 0.4,
                  transition: "opacity 0.3s ease",
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: item.color }} />
                <Typography sx={{ fontSize: 12 }}>{item.name}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Chart */}
      <Box sx={{ overflowX: "auto" }}>
        <Chart
          options={options}
          series={displaySeries}
          type="bar"
          height={height}
          width={categories.length * COLUMN_SLOT_PX}
        />
      </Box>
    </Box>
  );
};

export default ApexStackedColumnV2;
